import {
  type CraftJobNetworkSnapshot,
  getItemIcon,
  getItemName,
  getRecipeById,
  type ItemTypeValue,
  type RecipeDefinition,
} from '@dreamgame/shared';
import { useEffect, useRef, useState } from 'preact/hooks';
import { tv } from 'tailwind-variants';
import { sendCancelCraft, sendCraftOrder } from '../../network/NetworkClient';
import {
  activeRecipeCategory,
  closeWorkstationPanel,
  craftQueue,
  filteredRecipes,
  getMakeXQuantity,
  hideMakeXInput,
  isWorkstationPanelOpen,
  makeXInputVisible,
  makeXValue,
  openWorkstation,
  recipeCategories,
  selectedRecipe,
  selectRecipe,
  setActiveCategory,
  setMakeXValue,
  showMakeXInput,
  workstationFuel,
} from '../../state/crafting';
import { recipeName, t } from '../../state/i18n';
import { serverTick, serverTickIntervalMs, serverTickUpdatedAtMs } from '../../state/network';
import { getSelectedIds } from '../../state/selection';
import { CraftingFuelIndicator } from './CraftingFuelIndicator';
import {
  getCraftCompletionsNeededForDesiredOutput,
  getCraftOrderOutputTotals,
  getInterpolatedCraftProgress,
  getRemainingQueueOutputQuantity,
  getWorkstationTitleKey,
} from './craftingPanelUtils';
import { useWorkstationFuelBurnProgress } from './useWorkstationFuelBurnProgress';

// ============================================================================
// CraftingPanel - OSRS-style crafting interface
// ============================================================================

const panel = tv({
  slots: {
    root: ['fixed left-4 bottom-24', 'z-[1000]', 'flex items-start', 'animate-slide-up'],

    spine: [
      'w-4 h-[420px]',
      'bg-gradient-to-r from-leather-dark via-leather to-leather-dark',
      'border-y-2 border-l-2 border-leather-border',
      'rounded-l-md',
      'shadow-xl',
      'z-20',
      'relative',
    ],

    container: [
      'relative',
      'w-96 h-[400px]',
      'bg-parchment',
      'bg-[radial-gradient(ellipse_at_center,_var(--color-parchment-light)_0%,_var(--color-parchment)_100%)]',
      'border-y-4 border-r-4 border-leather',
      'rounded-r-lg',
      'shadow-tome',
      'flex flex-col',
      'z-10',
      'after:content-[""]',
      'after:absolute after:inset-2',
      'after:border-2 after:border-gold-filigree/30',
      'after:rounded-r-md',
      'after:pointer-events-none',
    ],

    cornerDecorTop: [
      'absolute top-2 right-2 w-8 h-8',
      'border-t-2 border-r-2 border-gold-filigree',
      'rounded-tr-lg',
      'pointer-events-none',
    ],
    cornerDecorBottom: [
      'absolute bottom-2 right-2 w-8 h-8',
      'border-b-2 border-r-2 border-gold-filigree',
      'rounded-br-lg',
      'pointer-events-none',
    ],

    header: [
      'relative',
      'flex items-center justify-center',
      'py-3',
      'mx-6 mt-2',
      'border-b border-gold-filigree/50',
    ],

    title: ['font-fantasy text-ink text-lg tracking-widest', 'uppercase', 'font-bold'],

    closeButton: [
      'absolute -right-3 -top-3',
      'w-8 h-8',
      'flex items-center justify-center',
      'rounded-full',
      'bg-leather-dark',
      'border-2 border-gold-filigree',
      'text-gold-bright',
      'hover:scale-110',
      'hover:bg-wax-seal',
      'transition-all duration-200',
      'cursor-pointer',
      'shadow-lg',
      'z-50',
    ],

    tabContainer: ['flex flex-col gap-1.5', 'mt-8', '-mr-2', 'z-0'],

    tab: [
      'w-32',
      'h-9',
      'pl-3 pr-4',
      'flex items-center justify-center',
      'bg-leather',
      'border-y border-l border-gold-filigree',
      'rounded-l-full',
      'text-xs font-fantasy tracking-wider font-bold',
      'text-parchment-dark',
      'shadow-md',
      'transition-all duration-200',
      'cursor-pointer',
      'hover:translate-x-[-4px]',
      'relative',
    ],

    tabActive: [
      'bg-leather-light',
      'text-gold-bright',
      'border-gold-bright',
      'translate-x-3',
      'z-30',
      'shadow-lg',
    ],

    tabLabel: ['flex-1', 'text-center', 'capitalize'],

    content: ['flex-1', 'flex flex-col', 'overflow-hidden'],

    recipeGrid: [
      'grid grid-cols-4 gap-1',
      'p-3',
      'overflow-y-auto',
      'scrollbar-thin scrollbar-thumb-leather scrollbar-track-transparent',
      'max-h-[140px]',
    ],

    recipeSlot: [
      'flex flex-col items-center justify-center',
      'p-1',
      'h-16 w-full',
      'rounded',
      'border border-transparent',
      'hover:bg-ink/5',
      'transition-all duration-150',
      'cursor-pointer',
    ],

    recipeSlotSelected: ['bg-ink/10', 'border-gold-filigree'],

    recipeName: ['text-[9px] text-ink font-bold text-center', 'leading-tight', 'mt-0.5'],

    recipeIcon: [
      'w-8 h-8',
      'bg-leather/20',
      'rounded',
      'flex items-center justify-center',
      'text-lg',
    ],

    detailsSection: [
      'mx-3 p-3',
      'border-t border-gold-filigree/30',
      'bg-parchment-dark/30',
      'rounded',
    ],

    detailsTitle: ['text-sm font-bold text-ink', 'mb-1'],

    detailsIngredients: ['text-xs text-ink-faded', 'mb-2'],

    makeButtons: ['flex gap-1', 'flex-wrap'],

    makeButton: [
      'px-2 py-1',
      'bg-leather',
      'border border-gold-filigree',
      'rounded',
      'text-xs font-bold text-parchment',
      'hover:bg-leather-light',
      'hover:text-gold-bright',
      'transition-all duration-150',
      'cursor-pointer',
    ],

    makeButtonPrimary: ['bg-leather-dark', 'border-gold-bright'],

    makeXInput: [
      'w-16 px-2 py-1',
      'bg-parchment',
      'border border-gold-filigree',
      'rounded',
      'text-xs text-ink',
      'focus:outline-none focus:border-gold-bright',
    ],

    queueSection: [
      'mx-3 mt-2 p-2',
      'border-t border-gold-filigree/30',
      'max-h-[100px]',
      'overflow-y-auto',
      'scrollbar-thin scrollbar-thumb-leather scrollbar-track-transparent',
    ],

    queueTitle: ['text-xs font-bold text-ink-faded', 'mb-1'],

    queueItem: ['flex items-center gap-2', 'py-1 px-2', 'bg-parchment-dark/20', 'rounded', 'mb-1'],

    queueProgress: ['flex-1', 'h-2', 'bg-leather/30', 'rounded-full', 'overflow-hidden'],

    queueProgressBar: ['h-full', 'bg-gold-filigree'],

    queueLabel: ['text-[10px] text-ink', 'flex-shrink-0'],

    queueCancel: [
      'w-4 h-4',
      'flex items-center justify-center',
      'text-wax-seal',
      'hover:text-red-600',
      'cursor-pointer',
      'flex-shrink-0',
    ],
  },
});

const getWorkstationName = (): string => {
  const workstationType = openWorkstation.value?.workstationType;
  return t(getWorkstationTitleKey({ workstationType }));
};

const RecipeSlot = ({
  recipe,
  isSelected,
  onSelect,
}: {
  recipe: RecipeDefinition;
  isSelected: boolean;
  onSelect: () => void;
}) => {
  const styles = panel();
  const outputItemType = recipe.outputs[0]?.itemType;
  const iconFile = outputItemType
    ? getItemIcon({ itemType: outputItemType as ItemTypeValue })
    : null;
  const texturePath = iconFile ? `/items/${iconFile}` : null;

  return (
    <button
      type="button"
      class={`${styles.recipeSlot()} ${isSelected ? styles.recipeSlotSelected() : ''}`}
      onClick={onSelect}
    >
      <div class={styles.recipeIcon()}>
        {texturePath ? (
          <img
            src={texturePath}
            alt={recipeName({ recipeId: recipe.id })}
            class="w-8 h-8 object-contain"
          />
        ) : (
          <span class="text-lg">?</span>
        )}
      </div>
      <span class={styles.recipeName()}>{recipeName({ recipeId: recipe.id })}</span>
    </button>
  );
};

const RecipeDetails = ({ recipe }: { recipe: RecipeDefinition }) => {
  const styles = panel();
  const showMakeX = makeXInputVisible.value;

  const handleMake = (desiredItems: number) => {
    const ws = openWorkstation.value;
    if (!ws) {
      return;
    }

    const selectedPawnIds = getSelectedIds();
    const completionsNeeded = getCraftCompletionsNeededForDesiredOutput({
      recipe,
      desiredOutputQuantity: desiredItems,
    });

    sendCraftOrder({
      workstationNetworkId: ws.networkId,
      recipeId: recipe.id,
      quantity: completionsNeeded,
      pawnIds: selectedPawnIds,
    });
  };

  const handleMakeX = () => {
    if (showMakeX) {
      const qty = getMakeXQuantity();
      if (qty > 0) {
        handleMake(qty);
        hideMakeXInput();
      }
    } else {
      showMakeXInput();
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      const qty = getMakeXQuantity();
      if (qty > 0) {
        handleMake(qty);
        hideMakeXInput();
      }
    } else if (e.key === 'Escape') {
      hideMakeXInput();
    }
  };

  const handleMakeAll = () => {
    const ws = openWorkstation.value;
    if (!ws) {
      return;
    }

    const selectedPawnIds = getSelectedIds();

    // Quantity 0 is a protocol sentinel: server crafts max available.
    sendCraftOrder({
      workstationNetworkId: ws.networkId,
      recipeId: recipe.id,
      quantity: 0,
      pawnIds: selectedPawnIds,
    });
  };

  return (
    <div class={styles.detailsSection()}>
      <div class={styles.detailsTitle()}>{recipeName({ recipeId: recipe.id })}</div>
      <div class={styles.detailsIngredients()}>
        {recipe.ingredients.map((ing, idx) => (
          <span key={idx}>
            {ing.quantity}x {getItemName({ itemType: ing.itemType as ItemTypeValue })}
            {idx < recipe.ingredients.length - 1 ? ', ' : ''}
          </span>
        ))}
        {' -> '}
        {recipe.outputs.map((out, idx) => (
          <span key={idx}>
            {out.quantity}x {getItemName({ itemType: out.itemType as ItemTypeValue })}
          </span>
        ))}
      </div>
      <div class={styles.makeButtons()}>
        <button
          type="button"
          class={`${styles.makeButton()} ${styles.makeButtonPrimary()}`}
          onClick={() => handleMake(1)}
        >
          {t('ui.buttons.make1')}
        </button>
        <button type="button" class={styles.makeButton()} onClick={() => handleMake(5)}>
          {t('ui.buttons.make5')}
        </button>
        <button type="button" class={styles.makeButton()} onClick={() => handleMake(10)}>
          {t('ui.buttons.make10')}
        </button>
        {showMakeX ? (
          <input
            type="text"
            class={styles.makeXInput()}
            value={makeXValue.value}
            onInput={(e) => setMakeXValue({ value: (e.target as HTMLInputElement).value })}
            onKeyDown={handleKeyDown}
            placeholder={t('ui.labels.qty')}
          />
        ) : (
          <button type="button" class={styles.makeButton()} onClick={handleMakeX}>
            {t('ui.buttons.makeX')}
          </button>
        )}
        <button type="button" class={styles.makeButton()} onClick={handleMakeAll}>
          {t('ui.buttons.makeAll')}
        </button>
      </div>
    </div>
  );
};

const QueueItem = ({ job }: { job: CraftJobNetworkSnapshot }) => {
  const styles = panel();
  const recipe = getRecipeById({ recipeId: job.recipeId });
  const craftTimeMs = recipe?.craftTimeMs ?? 3000;
  const { totalOutputQuantity, completedOutputQuantity } = getCraftOrderOutputTotals({ job });

  // Client-side interpolation for smooth progress bar
  const [displayProgress, setDisplayProgress] = useState(job.progress);
  const lastUpdateRef = useRef({ progress: job.progress, time: performance.now() });
  const animationRef = useRef<number>(0);

  useEffect(() => {
    // Update reference when server progress changes
    lastUpdateRef.current = { progress: job.progress, time: performance.now() };

    // Animate progress smoothly
    const animate = () => {
      const now = performance.now();
      const elapsed = now - lastUpdateRef.current.time;
      const serverProgress = lastUpdateRef.current.progress;

      const interpolatedProgress = getInterpolatedCraftProgress({
        serverProgress,
        elapsedMs: elapsed,
        craftTimeMs,
      });

      setDisplayProgress(interpolatedProgress);

      // Continue animation if not complete
      if (interpolatedProgress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [job.progress, craftTimeMs]);

  // Reset progress when quantityCompleted changes (new item starting)
  useEffect(() => {
    if (job.progress === 0) {
      setDisplayProgress(0);
      lastUpdateRef.current = { progress: 0, time: performance.now() };
    }
  }, [job.quantityCompleted, job.progress]);

  const handleCancel = () => {
    sendCancelCraft({ orderId: job.orderId });
  };

  const progressPercent = displayProgress * 100;

  return (
    <div class={styles.queueItem()}>
      <span class={styles.queueLabel()}>
        {recipeName({ recipeId: job.recipeId })} ({completedOutputQuantity}/{totalOutputQuantity})
      </span>
      <div class={styles.queueProgress()}>
        <div class={styles.queueProgressBar()} style={{ width: `${progressPercent}%` }} />
      </div>
      <button type="button" class={styles.queueCancel()} onClick={handleCancel} aria-label="Cancel">
        X
      </button>
    </div>
  );
};

export const CraftingPanel = () => {
  const styles = panel();

  if (!isWorkstationPanelOpen.value) {
    return null;
  }

  const categories = recipeCategories.value;
  const currentCategory = activeRecipeCategory.value;
  const recipes = filteredRecipes.value;
  const selected = selectedRecipe.value;
  const queue = craftQueue.value;
  const fuel = workstationFuel.value;
  const currentServerTick = serverTick.value;
  const currentServerTickIntervalMs = serverTickIntervalMs.value;
  const currentServerTickUpdatedAtMs = serverTickUpdatedAtMs.value;
  const displayBurnRemainingMs = useWorkstationFuelBurnProgress({
    fuel,
    currentServerTick,
    currentServerTickIntervalMs,
    currentServerTickUpdatedAtMs,
  });
  const remainingQueueOutputQuantity = getRemainingQueueOutputQuantity({ queue });

  const handleTabClick = (category: string) => {
    setActiveCategory({ category });
  };

  const handleRecipeSelect = (recipeId: string) => {
    selectRecipe({ recipeId });
  };

  const handleClose = () => {
    closeWorkstationPanel();
  };

  return (
    <div class={styles.root()}>
      {/* Category Tabs */}
      <div class={styles.tabContainer()}>
        {categories.map((category) => (
          <button
            type="button"
            key={category}
            class={`${styles.tab()} ${currentCategory === category ? styles.tabActive() : ''}`}
            onClick={() => handleTabClick(category)}
          >
            <span class={styles.tabLabel()}>{category}</span>
          </button>
        ))}
      </div>

      {/* Book Spine */}
      <div class={styles.spine()} />

      {/* Main Panel */}
      <div class={styles.container()}>
        {/* Close Button */}
        <button type="button" class={styles.closeButton()} onClick={handleClose} aria-label="Close">
          <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            class="w-4 h-4"
            role="img"
            aria-hidden="true"
          >
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
          </svg>
        </button>

        {/* Decorative Corners */}
        <div class={styles.cornerDecorTop()} />
        <div class={styles.cornerDecorBottom()} />

        {/* Header */}
        <div class={styles.header()}>
          <h2 class={styles.title()}>{getWorkstationName()}</h2>
        </div>

        {/* Content */}
        <div class={styles.content()}>
          {/* Recipe Grid */}
          <div class={styles.recipeGrid()}>
            {recipes.map((recipe) => (
              <RecipeSlot
                key={recipe.id}
                recipe={recipe}
                isSelected={selected?.id === recipe.id}
                onSelect={() => handleRecipeSelect(recipe.id)}
              />
            ))}
          </div>

          {/* Selected Recipe Details */}
          {selected && <RecipeDetails recipe={selected} />}

          {/* Queue Section */}
          {queue.length > 0 && (
            <div class={styles.queueSection()}>
              <div class={styles.queueTitle()}>
                {t('ui.labels.queue')} ({remainingQueueOutputQuantity})
              </div>
              {queue.map((job) => (
                <QueueItem key={job.orderId} job={job} />
              ))}
            </div>
          )}
        </div>

        {fuel && (
          <CraftingFuelIndicator fuel={fuel} displayBurnRemainingMs={displayBurnRemainingMs} />
        )}
      </div>
    </div>
  );
};
